import { Injectable } from "@angular/core";
import { AIServices } from "../ai/ai-services";
import { AIModelChangeService } from "../ai/model-changes/ai-model-change.service";
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model";
import { CURRENT_STATE_PROJECT_KEY, GraphData } from "../common/linqset-model";
import { defaultCapacityHours, Organisation, Project, totalModelInsights } from "../common/organisation";
import { assetInputs } from "../information/information-analysis-model";
import { RecommendationRequest, RecommendationsResponse } from "../common/process-recommendations";
import { buildEpmScorecard, epmComponents } from "../epm/epm-scorecard-model";
import { simulationEvidence, SimulationReport, SimulationSnapshot } from "./simulation-report";
import { HeldResults } from "./held-results";

// what the recommendations are worked out from: the current state's models shown, its analysis and organisation model,
// and the simulation run over them
export interface RecommendationSource {
    organisation: Organisation;
    modelKeys: string[];
    analysis: { nodes: AnalysisNode[], edges: AnalysisEdge[] };
    organisationModel: GraphData;
    report: SimulationReport;
    snapshot: SimulationSnapshot;
    currency: string;
    money: (value: number) => string;
    number: (value: number) => string;
}

// recommendations, with when they were worked out and whether they were held from an earlier request
export interface HeldRecommendations {
    response: RecommendationsResponse;
    at: Date;
    held: boolean;
}

// overall recommendations from AI, combining the EPM scorecard's evidence with the simulation's. Each set is held for
// the evidence it came from, so asking again for the same simulation shows it without waiting for AI
@Injectable({ providedIn: 'root' })
export class RecommendationsService {
    private held = new HeldResults<RecommendationsResponse>();

    constructor(private aiServices: AIServices, private modelChanges: AIModelChangeService) {}

    // the recommendations for the evidence, held from before unless asked for afresh
    async recommend(source: RecommendationSource, afresh = false): Promise<HeldRecommendations> {
        // the evidence decides what AI would say, so it's what they're held by; the model's context isn't needed for that
        const evidence = this.evidence(source);
        const key = `${source.organisation._key}|${JSON.stringify(evidence)}`;
        const held = this.held.get(key);
        if (held && !afresh) return { response: held.value, at: held.at, held: true };

        // the roles, systems and actions changes must name, as Change with AI reads them
        const prepared = await this.modelChanges.prepare(source.organisation, { _key: CURRENT_STATE_PROJECT_KEY, name: 'Current state' } as Project);
        const response = await this.aiServices.generateRecommendations(source.organisation._key, CURRENT_STATE_PROJECT_KEY, { ...evidence, context: prepared.context });
        return { response, at: this.held.set(key, response).at, held: false };
    }

    // what's known about the process: the EPM scorecard's evidence and the simulation's
    private evidence(source: RecommendationSource): Pick<RecommendationRequest, 'epm' | 'simulation'> {
        const { organisation, modelKeys } = source;
        const keys = new Set(modelKeys);
        const scoped = organisation.models.filter(m => keys.has(m._key));
        const totals = scoped.length ? totalModelInsights(scoped, CURRENT_STATE_PROJECT_KEY, organisation._key) : undefined;
        const scorecard = buildEpmScorecard({
            scopeName: scoped.length === 1 ? scoped[0].name : `The organisation (${scoped.length} models)`,
            workspaceName: 'Current state',
            modelKeys,
            nodes: source.analysis.nodes.filter(n => keys.has(n.modelKey)),
            edges: source.analysis.edges.filter(e => keys.has(e.modelKey)),
            components: epmComponents(source.organisationModel, CURRENT_STATE_PROJECT_KEY),
            modelNames: Object.fromEntries(organisation.models.map(m => [m._key, m.name])),
            defaultCapacity: defaultCapacityHours(organisation) > 0,
            totals: { revenue: totals?.outputRevenue || 0, value: totals?.outputValue || 0 },
            assets: assetInputs(organisation.informationAssets, totals, source.organisationModel.nodes),
            currency: source.currency,
            money: source.money,
            number: source.number,
        });
        return { epm: scorecard.evidence, simulation: simulationEvidence(source.report, source.snapshot) };
    }
}
